import { IRequest } from "../interfaces/authentication.interface";
import { ITranslationResponse, ITranslations } from "../interfaces/translations.interface";
import { strapiRequest } from "./requests.service";
import { DEFAULT_LANGUAGE, LOCALE } from "../constants";

type RawTranslation = ITranslationResponse["data"]["translations"][number];

const fetchRawTranslations = async (): Promise<RawTranslation[]> => {
    const query = `
            query translations($pagination: PaginationArg, $locale: I18NLocaleCode) {
                translations(pagination: $pagination, locale: $locale) {
                    documentId
                    key
                    value
                    locale
                }
            }
                        `;

    const variables = {
        pagination: {
            pageSize: 20000000
        },
        locale: "*"
    };

    const response = await strapiRequest.post<ITranslationResponse>(`/graphql`, {
        operationName: "translations",
        query,
        variables
    });

    return response.data.data.translations ?? [];
};

export const getTranslations = async (): Promise<IRequest<ITranslations>> => {
    try {
        const translations = await fetchRawTranslations();
        const parsedTranslations = parseTranslations({ data: { translations } });
        return { success: true, data: parsedTranslations };
    } catch (error: any) {
        return { success: false, data: error as any };
    }
};

const parseTranslations = (translations: ITranslationResponse): ITranslations => {
    const translationsMap = {};

    const assignLocale = (key: string, locale: string, value: string) => {
        if (Object.keys(LOCALE).findIndex((k) => LOCALE[k] === locale) < 0) return;
        Object.assign(translationsMap, {
            [`${key}-${locale}`]: value
        });
    };

    translations.data.translations.forEach((t) => {
        Object.assign(translationsMap, { [`${t.key}-id`]: t.documentId });

        assignLocale(t.key, t.locale, t.value);
    });

    return translationsMap as ITranslations;
};

export const setTranslation = async (
    key: string,
    translations: {
        id: string;
        [key: string]: string;
    }
): Promise<void> => {
    if (translations.id) {
        let id = await updateTranslation({
            id: translations.id,
            key,
            value: translations[DEFAULT_LANGUAGE],
            locale: DEFAULT_LANGUAGE
        });

        if (!id) {
            id = await postTranslation({
                key,
                value: translations[DEFAULT_LANGUAGE],
                locale: DEFAULT_LANGUAGE
            });
        }

        Object.keys(LOCALE).forEach((locale) => {
            updateTranslation({
                id: id as string,
                key,
                value: translations[locale],
                locale: LOCALE[locale]
            });
        });
    } else {
        const id = await postTranslation({
            key,
            value: translations[DEFAULT_LANGUAGE],
            locale: DEFAULT_LANGUAGE
        });

        if (!id) return;

        Object.keys(LOCALE).forEach((locale) => {
            if (locale === DEFAULT_LANGUAGE) return;
            updateTranslation({
                id: id as string,
                key,
                value: translations[locale],
                locale: LOCALE[locale]
            });
        });
    }
};

export const postTranslation = async (values: {
    key: string;
    value: string;
    locale: any;
}): Promise<string | null> => {
    try {
        const response = await strapiRequest.post<any>(
            `/api/translations?locale=${values.locale}`,
            {
                data: {
                    ...values
                }
            }
        );
        console.log(response);

        return response.data.data.documentId;
    } catch (e) {
        return null;
    }
};

export const updateTranslation = async (values: {
    id: string;
    key: string;
    value: string;
    locale: string;
}): Promise<string | null> => {
    if (values.value === "") return null;
    try {
        const response = await strapiRequest.put<any>(
            `/api/translations/${values.id}?locale=${values.locale}`,
            {
                data: {
                    key: values.key,
                    value: values.value,
                    locale: values.locale
                }
            }
        );
        return response.data.data.documentId;
    } catch (e) {
        return null;
    }
};

export const deleteTranslation = async (documentId: string, locale: string): Promise<boolean> => {
    try {
        await strapiRequest.delete(`/api/translations/${documentId}?locale=${locale}`);
        return true;
    } catch (e) {
        return false;
    }
};

export const deleteDuplicateTranslations = async (): Promise<{
    deleted: string[];
    failed: string[];
    kept: number;
}> => {
    const translations = await fetchRawTranslations();
    const documentsByKey = new Map<string, Map<string, string[]>>();

    translations.forEach((t) => {
        if (!documentsByKey.has(t.key)) {
            documentsByKey.set(t.key, new Map());
        }

        const documents = documentsByKey.get(t.key)!;
        const locales = documents.get(t.documentId) ?? [];
        locales.push(t.locale);
        documents.set(t.documentId, locales);
    });

    const toDelete: { documentId: string; locales: string[] }[] = [];
    let kept = 0;

    documentsByKey.forEach((documents) => {
        if (documents.size <= 1) {
            kept += 1;
            return;
        }

        let keepId = "";
        let keepLocaleCount = -1;

        documents.forEach((locales, documentId) => {
            if (locales.length >= keepLocaleCount) {
                keepId = documentId;
                keepLocaleCount = locales.length;
            }
        });

        kept += 1;

        documents.forEach((locales, documentId) => {
            if (documentId !== keepId) {
                toDelete.push({ documentId, locales });
            }
        });
    });

    const deleted: string[] = [];
    const failed: string[] = [];

    for (const { documentId, locales } of toDelete) {
        const results = await Promise.all(
            locales.map((locale) => deleteTranslation(documentId, locale))
        );

        if (results.every(Boolean)) {
            deleted.push(documentId);
        } else {
            failed.push(documentId);
        }
    }

    return { deleted, failed, kept };
};
